import { useState } from "react"
import { Form, Formik } from "formik"
import { Alert, Button, Modal } from "react-bootstrap"
import * as yup from 'yup'
import { useAppDispatch } from "../../../redux/store"
import { showError, showSuccess } from "../../../redux/counter/counterSlice"
import { useEditBookingMutation } from "../../../services/bClientApi"
import timeService from "../../../services/time/timeService"
import canAccess from "../../../services/accessService/accessService"
import { currencyFormatter } from "../../../Helpers/Parsers/currencyFormatter"
import { buildEarningsUpdate, getBaseEarnings, getConfirmedExtensions } from "../../../Helpers/Bookings/bookingHelpers"
import { PropertyBookingEntry } from "../../../Types/bookingTypes"
import { TextInputField } from "../../FormFields/TextInput/TextInputField"
import UserNameText from "../../Users/UserNameText"

interface BookingEarningsProps {
  bookingData: PropertyBookingEntry
}

type EditTarget = { type: 'total' } | { type: 'base' } | { type: 'extension', linkedService: string }

const DATE_FORMAT = 'MM/dd/yyyy'

export default function BookingEarnings({ bookingData }: BookingEarningsProps) {
  const dispatch = useAppDispatch()
  const [editBooking, editState] = useEditBookingMutation()
  const [editTarget, setEditTarget] = useState<EditTarget | null>(null)

  const canEdit = canAccess('routes', 'edit-booking', { payload: bookingData })

  const extensions = getConfirmedExtensions(bookingData)
  const extensionTotal = extensions.reduce((total: number, extension: any) => total + (+extension.payout || 0), 0)
  const baseEarnings = getBaseEarnings(bookingData)
  const nights = timeService.calculateNights(bookingData.checkIn, bookingData.checkOut)

  const targetExtension = editTarget?.type === 'extension'
    ? extensions.find((extension: any) => extension.linkedService === editTarget.linkedService)
    : null

  const getUpdate = (amount: number) => {
    if (editTarget?.type === 'extension') {
      return buildEarningsUpdate(bookingData, baseEarnings, { [editTarget.linkedService]: amount })
    }
    if (editTarget?.type === 'base') {
      return buildEarningsUpdate(bookingData, amount)
    }
    return buildEarningsUpdate(bookingData, amount - extensionTotal)
  }

  const handleSave = async (values: { earnings: string | number }) => {
    if (!bookingData._id || !editTarget) {
      return
    }

    try {
      await editBooking({
        bookingId: bookingData._id,
        bookingInfo: getUpdate(+values.earnings),
      }).unwrap()
      dispatch(showSuccess('Earnings updated'))
      setEditTarget(null)
    } catch (error: any) {
      dispatch(showError(error?.data?.error || 'Unable to update earnings'))
    }
  }

  const isTotal = editTarget?.type === 'total'
  const isBase = editTarget?.type === 'base'
  const minEarnings = isTotal ? extensionTotal : 0
  const currentAmount = isTotal ? bookingData.earnings : isBase ? baseEarnings : targetExtension?.payout

  const validationSchema = yup.object().shape({
    earnings: yup.number()
      .typeError('Earnings must be a number')
      .required('Earnings is required')
      .min(minEarnings, isTotal
        ? `Earnings must be at least the extension total (${currencyFormatter(minEarnings)})`
        : 'Earnings must be greater than or equal to zero'),
  })

  return (
    <div className="mb-4">
      <h3>Earnings</h3>

      <b>Earnings:</b> {currencyFormatter(bookingData.earnings)}{' '}
      {canEdit && (
        <Button
          size="sm"
          variant="outline-primary"
          className="py-0 ms-1"
          title="Edit earnings"
          onClick={() => setEditTarget({ type: 'total' })}
        >
          ✏️
        </Button>
      )}
      <br />
      <b>Price Per Night:</b> {currencyFormatter(bookingData.pricePerNight)}
      <small className="ms-1 text-muted">({nights} night{nights === 1 ? '' : 's'})</small>
      <br />

      {extensions.length > 0 && (
        <div className="mt-2">
          <b>Original Booking:</b> {currencyFormatter(baseEarnings)}
          {canEdit && (
            <Button
              size="sm"
              variant="outline-primary"
              className="py-0 ms-2"
              title="Edit original booking earnings"
              onClick={() => setEditTarget({ type: 'base' })}
            >
              ✏️
            </Button>
          )}
          <div className="mt-2">
            <b>Extensions</b>
            {extensions.map((extension: any, index: number) => (
              <div key={extension.linkedService} className="mb-2">
                <small>
                  Extension {index + 1}: {timeService.createLuxonDate(extension.originalCheckOut).toFormat(DATE_FORMAT)} - {timeService.createLuxonDate(extension.newCheckOut).toFormat(DATE_FORMAT)}
                  {' '}({currencyFormatter(extension.payout)})
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline-primary"
                      className="py-0 ms-2"
                      title="Edit extension earnings"
                      onClick={() => setEditTarget({ type: 'extension', linkedService: extension.linkedService })}
                    >
                      ✏️
                    </Button>
                  )}
                  {extension.paymentSource && (
                    <>
                      <br />
                      Payment Source: {extension.paymentSource}
                    </>
                  )}
                  {extension.createdBy && (
                    <>
                      <br />
                      Created By: <UserNameText userId={extension.createdBy} />
                    </>
                  )}
                </small>
              </div>
            ))}
          </div>
        </div>
      )}

      <Modal show={Boolean(editTarget)} onHide={() => setEditTarget(null)}>
        <Modal.Header closeButton>
          <Modal.Title>{isTotal ? 'Edit Earnings' : isBase ? 'Edit Original Booking Earnings' : 'Edit Extension Earnings'}</Modal.Title>
        </Modal.Header>
        <Formik
          key={editTarget ? `${editTarget.type}-${(editTarget as any).linkedService || ''}` : 'closed'}
          initialValues={{ earnings: currentAmount ?? '' }}
          validationSchema={validationSchema}
          onSubmit={handleSave}
        >
          {({ values, errors, isSubmitting }) => {
            const enteredAmount = values.earnings === '' ? NaN : +values.earnings
            const preview = Number.isFinite(enteredAmount) && editTarget ? getUpdate(enteredAmount) : null

            return (
              <Form>
                <Modal.Body>
                  <p className="mb-3">
                    {isTotal ? (
                      <>
                        Editing the <b>actual booking earnings</b><br />
                        Current earnings: <b>{currencyFormatter(bookingData.earnings)}</b>
                      </>
                    ) : isBase ? (
                      <>
                        Editing the <b>original booking earnings</b><br />
                        Current original earnings: <b>{currencyFormatter(baseEarnings)}</b>
                      </>
                    ) : targetExtension && (
                      <>
                        Editing an <b>extension</b>: {timeService.createLuxonDate(targetExtension.originalCheckOut).toFormat(DATE_FORMAT)} - {timeService.createLuxonDate(targetExtension.newCheckOut).toFormat(DATE_FORMAT)}<br />
                        Original extension earnings: <b>{currencyFormatter(targetExtension.payout)}</b>
                      </>
                    )}
                  </p>
                  <TextInputField
                    fieldLabel={isTotal ? 'Earnings' : isBase ? 'Original booking earnings' : 'Extension earnings'}
                    fieldName="earnings"
                    placeholder=""
                    fieldType="number"
                    required
                    disabled={editState.isLoading}
                  />
                  {preview && (
                    <Alert variant="info" className="mb-0">
                      New earnings: <strong>{currencyFormatter(preview.earnings)}</strong><br />
                      New price per night: <strong>{currencyFormatter(preview.pricePerNight)}</strong>
                    </Alert>
                  )}
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" type="button" onClick={() => setEditTarget(null)}>Cancel</Button>
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={editState.isLoading || isSubmitting || Object.keys(errors).length > 0}
                  >
                    {editState.isLoading ? 'Saving...' : 'Save'}
                  </Button>
                </Modal.Footer>
              </Form>
            )
          }}
        </Formik>
      </Modal>
    </div>
  )
}
